import React from 'react';
import { motion } from 'framer-motion';
import { trackCTA } from '../services/analytics';
import { scrollToSection } from '../utils/scroll';

export default function Hero() {
  return (
    <section
      id="hero"
      aria-label="Hero - Orbitra Solution"
      className="relative w-full min-h-[100dvh] min-h-[640px] overflow-hidden flex items-center justify-center pt-24 pb-16 md:py-28 bg-[#050505]"
    >
      {/* ─── Ambient Glow & Grid Background ─── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden select-none">
        {/* Refined Tech Grid Overlay (37% Opacity) */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.052)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.052)_1px,transparent_1px)] bg-[size:3.5rem_3.5rem] [mask-image:radial-gradient(ellipse_85%_75%_at_50%_45%,#000_80%,transparent_100%)] opacity-100" />

        {/* Radial background gradients */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_20%_40%,#1a0a3a_0%,transparent_60%)] opacity-80" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_80%_35%,#0a1a2e_0%,transparent_55%)] opacity-80" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_50%_at_50%_75%,#12082a_0%,transparent_65%)] opacity-70" />

        {/* Animated glowing orbs for subtle depth */}
        <motion.div
          className="absolute top-1/4 left-1/3 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-gradient-to-tr from-purple-600/15 to-indigo-500/10 blur-[100px]"
          animate={{
            x: [0, 30, -20, 0],
            y: [0, -30, 20, 0],
            scale: [1, 1.1, 0.95, 1],
          }}
          transition={{
            duration: 16,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute top-1/3 right-1/4 translate-x-1/2 -translate-y-1/2 w-[30rem] h-[30rem] rounded-full bg-gradient-to-br from-blue-600/10 to-cyan-500/10 blur-[120px]"
          animate={{
            x: [0, -40, 25, 0],
            y: [0, 25, -35, 0],
            scale: [1, 0.9, 1.08, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      {/* Radial vignette overlay */}
      <div className="absolute inset-0 z-[1] pointer-events-none bg-radial-[ellipse_at_center] from-transparent via-transparent to-[#050505]/70" />

      {/* ─── Hero Content ─── */}
      <div className="relative z-10 container mx-auto px-6 md:px-12 pointer-events-none flex flex-col items-center justify-center text-center mt-2 sm:mt-6 md:mt-10 mb-12 sm:mb-16 md:mb-20">

        {/* Status Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-md mb-8 pointer-events-auto shadow-[0_0_20px_rgba(255,255,255,0.03)]"
        >
          <span className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse" />
          <span className="text-[11px] uppercase tracking-[0.2em] font-medium text-gray-300">
            Next-Gen Digital Studio
          </span>
        </motion.div>

        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        >
          <h1 className="text-fluid-hero font-serif font-bold text-white max-w-4xl mx-auto leading-tight">
            We craft <span className="text-gradient">premium</span> digital experiences.
          </h1>
        </motion.div>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.42, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 md:mt-7 text-fluid-body text-gray-400 max-w-2xl mx-auto font-light leading-relaxed px-4"
        >
          Orbitra Solution builds bespoke websites, AI automation, and striking
          brands for businesses that refuse to blend in.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.64, ease: [0.16, 1, 0.3, 1] }}
          className="mt-9 md:mt-11 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 pointer-events-auto w-full sm:w-auto px-6"
        >
          <button
            type="button"
            onClick={() => {
              scrollToSection('contact');
              trackCTA('hero_initiate_project', 'contact');
            }}
            className="interactive group relative px-9 py-4 bg-white text-black text-sm uppercase tracking-widest font-semibold overflow-hidden rounded-full min-h-[48px] flex items-center justify-center w-full sm:w-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white cursor-pointer transition-shadow duration-300 hover:shadow-[0_0_25px_rgba(255,255,255,0.35)]"
          >
            <span className="relative z-10 group-hover:text-white transition-colors duration-500">
              Initiate Project
            </span>
            <div className="absolute inset-0 bg-black translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[0.16,1,0.3,1] z-0" />
          </button>
          <button
            type="button"
            onClick={() => {
              scrollToSection('work');
              trackCTA('hero_explore_work', 'work');
            }}
            className="interactive flex items-center justify-center gap-2 px-8 py-4 text-sm uppercase tracking-widest font-medium text-white hover:text-gray-300 transition-all duration-300 border border-white/10 rounded-full hover:border-white/30 bg-white/[0.02] hover:bg-white/[0.06] backdrop-blur-sm min-h-[48px] w-full sm:w-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white cursor-pointer"
          >
            Explore Work
          </button>
        </motion.div>
      </div>

      {/* ─── Scroll Indicator ─── */}
      <motion.div
        className="absolute bottom-3 sm:bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1.5 sm:gap-2 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 1 }}
      >
        <span className="text-[10px] uppercase tracking-[0.25em] text-gray-600">Scroll</span>
        <div className="w-px h-14 bg-white/10 overflow-hidden relative">
          <motion.div
            className="absolute top-0 left-0 w-full h-1/3 bg-white/50"
            animate={{ y: [0, 56] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: 'linear' }}
          />
        </div>
      </motion.div>
    </section>
  );
}
